/**
 * Shared utilities for task update components.
 * Extracts common logic for author display and date formatting.
 */
import { format as formatTz } from 'date-fns-tz';

import {
  TaskUpdate,
  TaskUpdateUser,
} from '@/services/types/client-intake-types';

const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * Type guard to check if update.user is a TaskUpdateUser object
 */
export const isTaskUpdateUser = (
  user: TaskUpdate['user'],
): user is TaskUpdateUser => {
  return typeof user === 'object' && user !== null;
};

/**
 * Get display name from a task update's author.
 * Handles user objects, string IDs, and agent names.
 */
export const getUpdateAuthorName = (update: TaskUpdate): string => {
  if (isTaskUpdateUser(update.user)) {
    const { first_name, last_name, username, email } = update.user;
    const fullName = [first_name, last_name].filter(Boolean).join(' ');
    if (fullName.trim()) return fullName;
    return username || email || 'Unknown User';
  }

  if (typeof update.user === 'string' && update.user) {
    return update.user;
  }

  if (update.agent) {
    return update.agent
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  return 'System';
};

/**
 * Get initials from update author name for avatar display.
 */
export const getAuthorInitials = (update: TaskUpdate): string => {
  const name = getUpdateAuthorName(update);
  if (!name) return 'S';

  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

/**
 * Format a date string for display in task updates.
 */
export const formatUpdateDate = (dateString: string): string => {
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return 'Invalid date';
  }

  return formatTz(date, 'MMM d, h:mm a', { timeZone: DEFAULT_TIMEZONE });
};
